import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { authService } from '../../../services/authService';
import { useAuth } from '../../../contexts/AuthContext';

const RegistrationForm = ({ onVerificationShow }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phoneNumber: '',
    companyName: '',
    state: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const stateOptions = [
    { value: '', label: 'Select State', disabled: true },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    const minLength = password?.length >= 8;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumber = /\d/?.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar
    };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData?.password);
      if (!passwordValidation?.isValid) {
        newErrors.password = 'Password does not meet all requirements';
      }
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.fullName) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.length < 2) {
      newErrors.fullName = 'Please enter your full name';
    }

    // Phone number is now optional - only validate format if provided
    if (formData?.phoneNumber && (!/^[\d\s()+-]+$/?.test(formData?.phoneNumber) || formData?.phoneNumber?.replace(/\D/g, '')?.length < 10)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Company name is now optional - no validation needed

    // State is now optional - no validation needed

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    const { data, error, emailConfirmationRequired } = await authService?.signUp({
      email: formData?.email,
      password: formData?.password,
      fullName: formData?.fullName,
      phoneNumber: formData?.phoneNumber,
      companyName: formData?.companyName,
      state: formData?.state
    });

    setIsSubmitting(false);

    if (error) {
      setApiError(error);
      return;
    }

    // Check if email verification is required
    if (emailConfirmationRequired) {
      setUserEmail(formData?.email);
      setShowVerificationMessage(true);
      onVerificationShow?.(true);
      
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Log out the user to prevent access before email verification
      await signOut();
      return;
    }

    // If no email confirmation required, redirect to dashboard
    navigate('/agent-dashboard');
  };

  const passwordValidation = validatePassword(formData?.password);

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      {showVerificationMessage ? (
        <div className="space-y-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Mail" size={32} className="text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Verify Your Email</h2>
            <div className="space-y-3">
              <p className="text-base text-muted-foreground">
                We've sent a verification email to:
              </p>
              <p className="text-lg font-semibold text-foreground">{userEmail}</p>
              <p className="text-base text-muted-foreground">
                Please check your inbox and click the verification link to activate your account.
              </p>
            </div>
          </div>

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">What's next?</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Return to the login page to access your account</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={() => navigate('/user-login')}
            >
              Go to Login Page
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              fullWidth
              onClick={() => {
                setShowVerificationMessage(false);
                onVerificationShow?.(false);
              }}
            >
              Back to Registration
            </Button>
          </div>
        </div>
      ) : (
        <>
          {apiError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{apiError}</p>
            </div>
          )}
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Account Information</h2>
              
              <Input
                type="email"
                label="Email Address"
                placeholder="agent@example.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
                description="We'll use this email for account verification and notifications"
              />

              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  placeholder="Create a strong password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  required
                  description="Must be at least 8 characters with uppercase, lowercase, number, and special character"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] p-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              {formData?.password && (
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-foreground mb-2">Password Requirements:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <PasswordRequirement met={passwordValidation?.minLength} text="At least 8 characters" />
                    <PasswordRequirement met={passwordValidation?.hasUpperCase} text="One uppercase letter" />
                    <PasswordRequirement met={passwordValidation?.hasLowerCase} text="One lowercase letter" />
                    <PasswordRequirement met={passwordValidation?.hasNumber} text="One number" />
                    <PasswordRequirement met={passwordValidation?.hasSpecialChar} text="One special character" />
                  </div>
                </div>
              )}

              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  error={errors?.confirmPassword}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] p-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">User Details</h2>
              <p className="text-sm text-muted-foreground">Tell us about yourself</p>

              <Input
                type="text"
                label="Full Name"
                placeholder="Enter your full name"
                value={formData?.fullName}
                onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                error={errors?.fullName}
                required
                description="Your first and last name"
              />

              <Input
                type="tel"
                label="Phone Number"
                placeholder="(555) 123-4567"
                value={formData?.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
                error={errors?.phoneNumber}
                description="Your contact phone number (optional)"
              />

              <Input
                type="text"
                label="Company Name"
                placeholder="Enter your company name"
                value={formData?.companyName}
                onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                error={errors?.companyName}
                description="Your real estate company or brokerage (optional)"
              />

              <Select
                label="State"
                options={stateOptions}
                value={formData?.state}
                onChange={(value) => handleInputChange('state', value)}
                error={errors?.state}
                searchable
                placeholder="Select your state"
                description="Your state of operation (optional)"
              />
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Terms & Conditions</h2>
              
              <div className="bg-muted rounded-lg p-4 md:p-6 space-y-4">
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowTerms(!showTerms)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-sm font-medium text-foreground">Terms of Service</span>
                    <Icon name={showTerms ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground" />
                  </button>
                  
                  {showTerms && (
                    <div className="text-sm text-muted-foreground space-y-2 pl-4 border-l-2 border-border">
                      <p>By using OfferSense, you agree to the following terms:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>You are a licensed real estate agent in good standing</li>
                        <li>You will use the platform solely for professional real estate purposes</li>
                        <li>You are responsible for the accuracy of all information entered</li>
                        <li>You will maintain the confidentiality of client information</li>
                        <li>You understand that AI-generated content is for informational purposes only</li>
                        <li>You will comply with all applicable real estate laws and regulations</li>
                        <li>Subscription fees are non-refundable except as required by law</li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowPrivacy(!showPrivacy)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-sm font-medium text-foreground">Privacy Policy</span>
                    <Icon name={showPrivacy ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground" />
                  </button>
                  
                  {showPrivacy && (
                    <div className="text-sm text-muted-foreground space-y-2 pl-4 border-l-2 border-border">
                      <p>We are committed to protecting your privacy:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Your personal information is encrypted and securely stored</li>
                        <li>We do not sell or share your data with third parties</li>
                        <li>Property and offer data remains confidential</li>
                        <li>Email communications are limited to essential notifications</li>
                        <li>You can request data deletion at any time</li>
                        <li>We comply with all applicable data protection regulations</li>
                        <li>Cookies are used only for essential functionality</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <Checkbox
                label={
                  <span className="text-sm">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-primary hover:underline font-medium"
                    >
                      Terms of Service
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacy(true)}
                      className="text-primary hover:underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </span>
                }
                checked={formData?.acceptTerms}
                onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
                error={errors?.acceptTerms}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                iconName="UserPlus"
                iconPosition="left"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => navigate('/user-login')}
                disabled={isSubmitting}
              >
                Already have an account?
              </Button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

const PasswordRequirement = ({ met, text }) => (
  <div className="flex items-center gap-2">
    <Icon
      name={met ? 'CheckCircle2' : 'Circle'}
      size={16}
      className={met ? 'text-success' : 'text-muted-foreground'}
    />
    <span className={`text-xs ${met ? 'text-success' : 'text-muted-foreground'}`}>
      {text}
    </span>
  </div>
);

export default RegistrationForm;